// /features/posts/PostList.js

import React, { lazy, Suspense, useEffect, useState, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import { useUser } from '../../context/UserContext';
import { useThemeContext } from '../../context/ThemeContext';
import axios from '../../services/axios';
import { formatPostDate } from './postUtils';
import { fetchReactionTypes, sendReaction, deleteReaction } from '../../services/reactions';
import CommentList from '../comments/CommentList.js';
import { momentLabelFromAuthor, bestAuthorName } from "./postUtils";


const MapDisplay = lazy(() => import('../map/MapDisplay'));

// Animations
const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const pop = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.6); }
  100% { transform: scale(1); }
`;

// Styled components
const PostCard = styled.div`
  background: ${({ theme }) => theme.cardBg || '#fff'};
  padding: 15px;
  border-radius: 8px;
  box-shadow: ${({ theme }) => theme.cardShadow || '0 2px 5px rgba(0, 0, 0, 0.1)'};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 40px;
  color: ${({ theme }) => theme.text || '#000'};

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ImageGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
`;

const EditButton = styled.button`
  padding: 6px 12px;
  background-color: ${({ theme }) => theme.primary || '#007bff'};
  color: white;
  border: none;
  border-radius: 4px;
  margin-right: 10px;
  cursor: pointer;
`;

const DeleteButton = styled.button`
  padding: 6px 12px;
  background-color: ${({ theme }) => theme.danger || '#dc3545'};
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
`;

const ReactionButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 10px;
  margin: 10px 0;
  width: 100%;
  box-sizing: border-box;
`;

const ReactionButton = styled.button`
  font-size: 1.5rem;
  border: none;
  background: transparent;
  cursor: pointer;
  max-width: 100%;
  word-wrap: break-word;

  &.popped {
    animation: ${pop} 300ms ease;
  }
`;

const VideoSpinner = styled.div`
  width: 24px;
  height: 24px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top-color: ${({ theme }) => theme.primary || '#007bff'};
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;

const InteractionRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
  margin-top: 20px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const EditControls = styled.div`
  flex: 0 0 auto; /* only as wide as content */
`;

const CommentsContainer = styled.div`
  flex: 1;
  min-width: 280px;
  max-width: 100%;
`;

const MapContainer = styled.div`
  margin-left: 20px;
  min-width: 300px;
  max-width: 100%;
  flex: 1 1 300px;
  overflow: hidden;

  @media (max-width: 768px) {
    margin-left: 0;
    width: 100%;
    flex: 1 1 100%;
  }
`;

const SectionBlock = styled.div`
  border: 1px solid ${({ theme }) => theme.border || '#ccc'};
  border-radius: 8px;
  padding: 10px;
  background-color: ${({ theme }) => theme.cardInner || '#fff'};
  margin-bottom: 10px;
`;

const SectionMeta = styled.div`
  font-size: 0.8rem;
  opacity: 0.75;
  margin-bottom: 6px;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;


const PostList = ({ posts = [], onEdit, onDelete, onImageClick }) => {
  const { user } = useUser();
  const { can } = useAuth();
  const { theme } = useThemeContext();
  const imageRefs = useRef({});
  const [localPosts, setPosts] = useState(posts);
  const [reactionTypes, setReactionTypes] = useState([]);
  const seenSectionViews = useRef(new Set());
  const seenMediaViews = useRef(new Set());

  useEffect(() => {
    fetchReactionTypes().then(setReactionTypes).catch(err =>
      console.error('Error fetching emoji types', err)
    );
  }, []);

  useEffect(() => {
    setPosts(posts);
  }, [posts]);

  useEffect(() => {
    const sectionsToTrack = [];
    for (const p of localPosts || []) {
      const secs = Array.isArray(p?.sections) ? p.sections : [];
      for (const s of secs) {
        if (!s?.id) continue;
        const key = `section:${s.id}`;
        if (seenSectionViews.current.has(key)) continue;
        sectionsToTrack.push({ sectionId: s.id, postId: p.id });
        seenSectionViews.current.add(key);
      }
    }

    if (sectionsToTrack.length === 0) return;

    sectionsToTrack.forEach(({ sectionId, postId }) => {
      axios.post('/api/metrics/track/', {
        event_type: 'section.viewed',
        entity_type: 'section',
        entity_id: sectionId,
        post_id: postId,
        section_id: sectionId,
      }).catch(() => {});
    });
  }, [localPosts]);

  const trackMediaView = (media, postId, sectionId = null) => {
    if (!media?.id) return;
    const key = `media:${media.id}`;
    if (seenMediaViews.current.has(key)) return;
    seenMediaViews.current.add(key);

    axios.post('/api/metrics/track/', {
      event_type: 'media.viewed',
      entity_type: 'media',
      entity_id: media.id,
      post_id: postId,
      section_id: sectionId,
    }).catch(() => {});
  };

  const updatePostReactions = (postId, newReaction, deletedId = null) => {
    setPosts(prevPosts =>
      prevPosts.map(post => {
        if (post.id !== postId) return post;
        let updatedReactions = [...(post.reactions || [])];

        if (deletedId) {
          updatedReactions = updatedReactions.filter(r => r.id !== deletedId);
        } else if (newReaction) {
          const exists = updatedReactions.find(
            r =>
              r.reaction_type.id === newReaction.reaction_type.id &&
              r.user === newReaction.user
          );
          if (!exists) updatedReactions.push(newReaction);
        }

        return { ...post, reactions: updatedReactions };
      })
    );
  };


  const getRenderableSections = (post) => {
    const secs = Array.isArray(post?.sections) ? post.sections : [];

    if (secs.length > 0) {
      return secs
        .map(s => ({
          id: s.id ?? `${post.id}-order-${s.order}`,
          order: Number(s.order) || 1,
          title: s.title || '',
          content: s.content || '',
          author: s.author || null,
          post_id: post.id,
          media: Array.isArray(s.media) ? s.media : [],   // ✅ FIX
        }))
        .sort((a, b) => a.order - b.order);
    }

    // legacy fallback
    return [{
      id: `${post.id}-legacy`,
      order: 1,
      title: '',
      content: post?.content || '',
      author: post?.owner || null,
      post_id: post.id,
      media: [], // legacy posts had no section media
    }];
  };

  const renderSectionMedia = (section, onImageClick, imageRefs, theme) => {
    if (!Array.isArray(section.media) || section.media.length === 0) return null;

    return (
      <ImageGrid>
        {section.media.map((media, i) => {
          if (media.content_type?.startsWith('image')) {
            return (
              <picture key={media.id}>
                {media.webp_url && <source srcSet={media.webp_url} type="image/webp" />}
                <img
                  ref={el => (imageRefs.current[media.id] = el)}
                  src={media.thumbnail_url || media.media_url}
                  alt="thumbnail"
                  width={100}
                  height={100}
                  loading="lazy"
                  style={{ cursor: 'pointer', objectFit: 'cover', borderRadius: 4 }}
                  onClick={() => {
                    trackMediaView(media, section?.post_id || null, section?.id || null);
                    onImageClick(section.media, i);
                  }}
                />
              </picture>
            );
          }

          if (media.content_type?.startsWith('video')) {
            const processed = !!media.processed;
            const poster = media.poster_url || media.thumbnail_url || null;
            const canOpen = processed && !!media.media_url;

            return (
              <div
                key={media.id}
                style={{
                  position: 'relative',
                  width: 100,
                  height: 56,
                  borderRadius: 4,
                  overflow: 'hidden',
                  backgroundColor: '#eee',
                  cursor: canOpen ? 'pointer' : 'default',
                  opacity: canOpen ? 1 : 0.6,
                }}
                onClick={() => {
                  if (!canOpen) return;
                  trackMediaView(media, section?.post_id || null, section?.id || null);
                  onImageClick(section.media, i);
                }}
              >
                {poster ? (
                  <img
                    src={poster}
                    alt="video thumbnail"
                    width={100}
                    height={56}
                    style={{ objectFit: 'cover' }}
                  />
                ) : (
                  <div style={{ width: '100%', height: '100%', backgroundColor: '#ddd' }} />
                )}

                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    pointerEvents: 'none',
                  }}
                >
                  {!processed ? (
                    <VideoSpinner theme={theme} />
                  ) : (
                    <span style={{ fontSize: 28, color: 'white', textShadow: '0 0 8px rgba(0,0,0,0.7)' }}>
                      ►
                    </span>
                  )}
                </div>
              </div>
            );
          }

          return null;
        })}
      </ImageGrid>
    );
  };


  return (
    <div className="post-list">
      {localPosts.map(post => {
        const displayDate = formatPostDate(post.post_date || post.created_at, post.timezone);
        const center = {
          lat: parseFloat(post.latitude),
          lng: parseFloat(post.longitude),
        };
        if (!center || isNaN(center.lat) || isNaN(center.lng)) return null;

        const userReactions = post.reactions || [];
        const canEdit = !!post?.can_edit_post;

        const canDelete = can('posts:delete_any') ||
        (can('posts:delete_own') && post.owner === user?.username); // or owner_display not ideal   

        return (
          <PostCard key={post.id} theme={theme}>
            <div style={{ flex: '1 1 300px', minWidth: 0 }}>
              <h2>{post.title}</h2>
              <div style={{ marginBottom: 10, fontSize: '0.9rem', color: theme.subtleText || '#555' }}>
                🕒 <strong>Date:</strong> {displayDate}
                <br />
                ✍️ <strong>By:</strong> {post.owner_display || post.owner}
              </div>

              {getRenderableSections(post).map((s) => (
                <SectionBlock key={s.id} theme={theme}>
                  <SectionMeta theme={theme}>
                    <span>
                      <strong>{momentLabelFromAuthor(bestAuthorName(s) || s.author)}</strong>
                    </span>
                  </SectionMeta>

                  {s.title && (
                    <div style={{ fontWeight: 700, marginBottom: 6 }}>
                      {s.title}
                    </div>
                  )}

                  <div dangerouslySetInnerHTML={{ __html: s.content }} />

                  {/* ✅ Moment media appears HERE */}
                  {renderSectionMedia(s, onImageClick, imageRefs, theme)}
                </SectionBlock>
              ))}


              {post.blog?.allow_reactions && post.reactions?.length > 0 && (
                <ReactionButtonRow>
                  {Object.entries(
                    userReactions.reduce((acc, r) => {
                      const key = r.reaction_type?.id;
                      if (!key) return acc;
                      if (!acc[key]) {
                        acc[key] = { type: r.reaction_type, count: 1 };
                      } else {
                        acc[key].count += 1;
                      }
                      return acc;
                    }, {})
                  ).map(([id, { type, count }]) => (
                    <span key={id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      {type.image ? (
                        <img
                          src={type.image}
                          alt={type.label}
                          style={{ width: 20, height: 20, verticalAlign: 'middle' }}
                        />
                      ) : (
                        type.emoji
                      )}
                      {count}
                    </span>
                  ))}

                </ReactionButtonRow>
              )}

              {post.blog?.allow_reactions && reactionTypes.length > 0 && (
                <ReactionButtonRow>
                  {reactionTypes.map(type => (
                    <ReactionButton
                      key={type.id}
                      onClick={async e => {
                        const button = e.currentTarget;
                        button.classList.add('popped');
                        setTimeout(() => button.classList.remove('popped'), 300);

                        const existing = userReactions.find(
                          r =>
                            r.reaction_type.id === type.id &&
                            r.user === user?.username
                        );

                        try {
                          if (existing) {
                            await deleteReaction(existing.id);
                            updatePostReactions(post.id, null, existing.id);
                          } else {
                            const newReaction = await sendReaction(post.id, type.id);
                            updatePostReactions(post.id, newReaction);
                          }
                        } catch (err) {
                          console.error('Error toggling reaction:', err);
                        }
                      }}
                      title={type.label}
                    >
                      {type.image ? (
                        <img
                          src={type.image}
                          alt={type.label}
                          title={type.label}
                          style={{
                            width: 24,
                            height: 24,
                            verticalAlign: 'middle',
                            borderRadius: 4,
                            objectFit: 'contain',
                          }}
                        />
                      ) : (
                        type.emoji
                      )}
                    </ReactionButton>
                  ))}
                </ReactionButtonRow>
              )}

              {Array.isArray(post.post_media) && post.post_media.length > 0 && (
                <>
                  <h4 style={{ marginTop: 20, opacity: 0.7 }}>Post Inbox</h4>
                  <ImageGrid>
                    {post.post_media.map((media, i) => {
                      const isVideo = media.content_type?.startsWith('video');
                      const poster = media.poster_url || media.thumbnail_url || null;

                      return (
                        <div key={media.id} style={{ width: 100, height: 100 }}>
                          {isVideo ? (
                            <div
                              style={{
                                position: 'relative',
                                width: 100,
                                height: 56,
                                borderRadius: 4,
                                overflow: 'hidden',
                                backgroundColor: '#eee',
                                cursor: media.processed ? 'pointer' : 'default',
                                opacity: media.processed ? 1 : 0.6,
                              }}
                              onClick={() => {
                                if (!media.processed) return;
                                trackMediaView(media, post.id, null);
                                onImageClick(post.post_media, i);
                              }}
                            >
                              {poster ? (
                                <img src={poster} alt="video thumbnail" width={100} height={56} style={{ objectFit: 'cover' }} />
                              ) : (
                                <div style={{ width: '100%', height: '100%', backgroundColor: '#ddd' }} />
                              )}
                              <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', pointerEvents: 'none' }}>
                                {!media.processed ? <VideoSpinner theme={theme} /> : <span style={{ fontSize: 28, color: 'white', textShadow: '0 0 8px rgba(0,0,0,0.7)' }}>►</span>}
                              </div>
                            </div>
                          ) : (
                            <img
                              src={media.thumbnail_url || media.media_url}
                              width={100}
                              height={100}
                              style={{ objectFit: 'cover', borderRadius: 4, cursor: 'pointer' }}
                              onClick={() => {
                                trackMediaView(media, post.id, null);
                                onImageClick(post.post_media, i);
                              }}
                              alt="media"
                            />
                          )}
                        </div>
                      );
                    })}

                  </ImageGrid>
                </>
              )}


              <InteractionRow>
                <EditControls>
                  {(canEdit || canDelete) && (
                    <>
                      {canEdit && (
                        <EditButton theme={theme} onClick={() => onEdit(post)}>
                          Edit
                        </EditButton>
                      )}
                      {canDelete && (
                        <DeleteButton theme={theme} onClick={() => onDelete(post.id)}>
                          Delete
                        </DeleteButton>
                      )}
                    </>
                  )}
                </EditControls>

                <CommentsContainer theme={theme}>
                  <CommentList postId={post.id} user={user} allowComments={post.blog?.allow_comments} />
                </CommentsContainer>
              </InteractionRow>

            </div>  {/* closes left side column */}

            <MapContainer>
              <Suspense fallback={<div style={{ width: 300, height: 300 }}>Loading map...</div>}>
                <MapDisplay
                  lat={post.latitude}
                  lng={post.longitude}
                  locationName={post.location}
                  timeZoneId={post.timezone}
                  apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                />
              </Suspense>
            </MapContainer>

          </PostCard>
        );
      })}
    </div>
  );
};

export default PostList;
